import { Handle, Position, type NodeProps } from "@xyflow/react";
import React from "react";

import { isCollapsedNode } from "./collapse";
import { nodeVisual } from "./contextGraphTheme";
import { showsSublabel, type ContextFlowNode } from "./layout";

/**
 * One card in the context graph.
 *
 * A single node type switching on `kind` rather than eight registered types:
 * the cards differ only in paint and in whether a chip is present, and eight
 * near-identical components is how the company card and the metric card end up
 * with different padding.
 *
 * Eight kinds separated by tint alone asked the reader to hold a colour key in
 * their head, so each card also carries a 4px accent bar in its kind's colour
 * down the left edge — the same swatch the legend shows. Shape and position
 * carry the meaning; the tint only reinforces it.
 *
 * The box model here is mirrored by `measureNode` in `layout.ts` — dagre has to
 * size the card before it exists. Change one and change the other.
 */
const HIDDEN_HANDLE = { opacity: 0, pointerEvents: "none" } as const;

const HANDLE_SIDES = [
	["left", Position.Left],
	["right", Position.Right],
	["top", Position.Top],
	["bottom", Position.Bottom],
] as const;

const ContextGraphNode: React.FC<NodeProps<ContextFlowNode>> = ({ data, dragging }) => {
	const { node, onPath, dimmed, size } = data;
	const visual = nodeVisual(node, onPath);
	// A "+N" card stands in for nodes that are not drawn. Dashed, so it reads as
	// a door rather than as a thing the answer cited.
	const collapsed = isCollapsedNode(node);

	return (
		<div
			data-testid={`context-node-${node.id}`}
			data-kind={node.kind}
			data-on-path={onPath ? "true" : "false"}
			data-collapsed={collapsed ? "true" : "false"}
			data-dimmed={dimmed ? "true" : "false"}
			className="context-graph__card"
			style={{
				width: size.width,
				minHeight: size.height,
				background: visual.background,
				border: `${visual.borderWidth}px ${collapsed ? "dashed" : "solid"} ${visual.borderColor}`,
				color: visual.color,
				// Dimming is opacity rather than a second palette: it has to read
				// as "still there, just not this question".
				opacity: dimmed ? 0.45 : 1,
				// The accent is a real element rather than a border-left so the
				// dashed "+N" outline still runs the whole way round the card.
				["--cg-card-accent" as string]: visual.accent,
				// A card being dragged lifts; the lift is suppressed on the rest so
				// a still canvas is flat and calm.
				boxShadow: dragging
					? "0 12px 28px rgba(15, 32, 39, 0.18)"
					: onPath
						? "0 2px 10px rgba(0, 76, 75, 0.14)"
						: "0 1px 3px rgba(15, 32, 39, 0.06)",
			}}
		>
			<span
				className="context-graph__accent"
				aria-hidden="true"
				style={{ background: visual.accent }}
			/>

			{/* A source and a target anchor on all four sides, all invisible.
			    `layout.ts` picks the pair per edge from the two cards' relative
			    position: a card's own edges leave whichever side actually faces
			    where they are going. With only Left/Right, an edge running back
			    up the graph — the contract's `company —GOVERNED_BY→ document` —
			    doubled back on itself and drew a loop that read as a phantom
			    empty card. */}
			{HANDLE_SIDES.map(([side, position]) => (
				<React.Fragment key={side}>
					<Handle
						type="target"
						id={`t-${side}`}
						position={position}
						isConnectable={false}
						style={HIDDEN_HANDLE}
					/>
					<Handle
						type="source"
						id={`s-${side}`}
						position={position}
						isConnectable={false}
						style={HIDDEN_HANDLE}
					/>
				</React.Fragment>
			))}

			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<span
					style={{
						fontSize: 14.5,
						lineHeight: "21px",
						fontWeight: 600,
						letterSpacing: "-0.01em",
					}}
				>
					{node.label}
				</span>
				{node.badge && (
					<span
						style={{
							flexShrink: 0,
							fontSize: 10,
							lineHeight: "15px",
							fontWeight: 600,
							letterSpacing: "0.06em",
							padding: "0 6px",
							borderRadius: 999,
							border: `1px solid var(--cg-badge-border)`,
							color: "var(--cg-badge-text)",
						}}
					>
						{node.badge}
					</span>
				)}
			</div>

			{showsSublabel(node) && (
				<div
					style={{
						fontSize: 13,
						lineHeight: "18px",
						color: visual.sublabelColor,
					}}
				>
					{node.sublabel}
				</div>
			)}
		</div>
	);
};

export default ContextGraphNode;
